import * as React from "react";
import { Alert, Card } from "antd";
import { CardTitle } from "../../../components/antd/card/CardTitle";
import { CardContent } from "../../../components/antd/card/CardContent";
import { formSet, formSetValue, useCpFormValue } from "../../gvc/testNewForm/utils";
import { CpFormItem } from "../../gvc/testNewForm/CpFormItem";
import { FormItem } from "../../../components/antd/form/FormItem";
import { FormLabel } from "../../../components/antd/form/FormLabel";
import { FormItemValue } from "../../../components/antd/form/FormItemValue";
import { CpInput } from "../../gvc/testNewForm/CpInput";
import { CpLabel } from "../../gvc/testNewForm/CpLabel";
import { CpInputDescription } from "../../gvc/testNewForm/CpInputDescription";
import { parseLink } from "../../../utils/linkParser/linkParser";
import { CpErrorWarning } from "../../gvc/testNewForm/CpErrorWarning";
import { useDebounce } from "../../../components/table/useDebounce";
import { store } from "../../../store";
import { Ownership } from "./ownership";
import { DnsConfig } from "./dnsConfig";
import { WorkloadGVCTagFn } from "../../../components/antd/kindselect/tagRenderers/workloadGvcTag";
import { CpErrorWarningArrayIcon } from "../../gvc/testNewForm/CpErrorWarningArrayIcon";
import { useCpFormContext } from "../../../components/form/useCpFormContext";
import { CpKindSelect } from "../../gvc/testNewForm/temp/CpKindSelect";
import { request } from "../../../services/cpln";
import { CpTagsNew } from "../../../components/form/CpTagsNew";
import { isInternalDomain } from "../../../services/utils";

interface Props {}
export const Basic: React.FC<Props> = () => {
  const { id } = useCpFormContext();
  const draftType = useCpFormValue(`meta.draftType`);
  const isEdit = draftType === "edit";
  const name = useCpFormValue("basic.name") || "";
  const isInternal = React.useMemo(() => isInternalDomain(name), [name]);
  const workload = useCpFormValue("basic.workload");

  const [debouncedName, setDebouncedName] = useDebounce(name, 300);
  React.useEffect(() => {
    setDebouncedName(name);
  }, [name]);
  React.useEffect(() => {
    const form = store.getState().form.instances[id];
    const onBlurCounter = form.meta.onBlurCounter;
    formSet(id, `meta.onBlurCounter`, onBlurCounter + 1);
  }, [debouncedName]);

  React.useEffect(() => {
    const link = workload.link;
    const parsedLink = parseLink(link, { useInputCtx: true });
    const gvcLink = `/org/${parsedLink.org}/gvc/${parsedLink.gvc}`;
    formSetValue(id, "gvc.value", parsedLink.gvc);
    formSetValue(id, "gvc.link", gvcLink);

    // Fetch GVC item to get alias for DNS config display
    if (gvcLink && parsedLink.gvc) {
      request({ url: gvcLink })
        .then(({ data }) => {
          formSetValue(id, "gvc.item", data);
        })
        .catch(() => {
          // Ignore errors - alias display is optional
        });
    }
  }, [workload]);

  return (
    <>
      <div className="cpln-form-section cpln-ant-card-container" data-section-id={"general"}>
        <Card>
          <CardTitle>
            Domain
            <CpErrorWarningArrayIcon names={[`basic.name`, `basic.description`, `basic.workload`]} />
          </CardTitle>
          <CardContent>
            {isEdit ? (
              <FormItem>
                <FormLabel>Domain</FormLabel>
                <FormItemValue>{name}</FormItemValue>
              </FormItem>
            ) : (
              <CpFormItem>
                <CpLabel name={`basic.name`}>Domain</CpLabel>
                <CpInput name={`basic.name`} style={{ maxWidth: 600 }} />
                <CpErrorWarning name={`basic.name`} />
              </CpFormItem>
            )}
            {isInternal ? (
              <Alert
                showIcon
                type="warning"
                className="w-fit my-4"
                message="Internal domains are only available in advanced mode. Please switch to advanced mode using the button in the sidebar."
              />
            ) : (
              <>
                <div style={{ maxWidth: 600 }}>
                  <CpInputDescription namePath="basic.name" descriptionPath="basic.description" />
                </div>
                <CpFormItem>
                  <CpLabel name={`basic.workload`}>Workload</CpLabel>
                  <CpKindSelect
                    name="basic.workload"
                    optionValue="relative"
                    kind="workload"
                    style={{ maxWidth: 600 }}
                    tagRenderers={[WorkloadGVCTagFn(false)]}
                  />
                  <CpErrorWarning name={`basic.workload`} />
                </CpFormItem>
                <CpFormItem>
                  <div className="flex flex-col gap-10">
                    <Ownership />
                    <DnsConfig />
                  </div>
                </CpFormItem>
              </>
            )}
          </CardContent>
        </Card>
      </div>
      <CpTagsNew />
    </>
  );
};
